import React, { useState, useEffect, useRef } from 'react';
import '../styles/PlayerSettingsDialog.css';
import config from '../config';
import { generateColorMatrix } from '../utils/colors';

interface PlayerSettingsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (p1n: string, p2n: string, p1c: string, p2c: string, p1d: any, p2d: any) => void;
  initialPlayer1Name: string;
  initialPlayer2Name: string;
  initialPlayer1Color: string;
  initialPlayer2Color: string;
  player1Details: any;
  player2Details: any;
  targetPlayer: 1 | 2;
}

const PlayerSettingsDialog: React.FC<PlayerSettingsDialogProps> = ({
  isOpen, onClose, onSave,
  initialPlayer1Name, initialPlayer2Name,
  initialPlayer1Color, initialPlayer2Color,
  player1Details: initialP1Details,
  player2Details: initialP2Details,
  targetPlayer
}) => {
  console.log("PlayerSettingsDialog.tsx LOADED - VERSION 3.1.6 - @ " + new Date().toISOString());
  // Lokale states voor formulier
  const [player1Name, setPlayer1Name] = useState(initialPlayer1Name);
  const [player2Name, setPlayer2Name] = useState(initialPlayer2Name);
  const [player1Color, setPlayer1Color] = useState(initialPlayer1Color);
  const [player2Color, setPlayer2Color] = useState(initialPlayer2Color);

  // States voor de opgehaalde details
  const [p1Details, setP1Details] = useState(initialP1Details);
  const [p2Details, setP2Details] = useState(initialP2Details);

  // Loading en Counters
  const [loading1, setLoading1] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [counter1, setCounter1] = useState(0);
  const [counter2, setCounter2] = useState(0);

  // Refs voor intervals om memory leaks te voorkomen
  const interval1Ref = useRef<NodeJS.Timeout | null>(null);
  const interval2Ref = useRef<NodeJS.Timeout | null>(null);
  const colorMatrix = generateColorMatrix();

  const cleanPlayerName = (name: string) => {
    if (!name) return name;
    // Split on '(' or ' - ' or '  ' (double space often precedes club/level in search)
    let cleaned = name.split(/[(-]/)[0].trim();

    // Remove terminal 3-4 letter country codes that might be stuck to the name (e.g. "VAN SCHAIKNLD")
    // This is tricky if the name itself ends in 3 uppercase letters, 
    // but usually SquashLevels names are mixed case or followed by space then uppercase.
    // If the name is uppercase like "VAN SCHAIK", and it's followed by "NLD", 
    // it usually looks like "VAN SCHAIK NLD".
    cleaned = cleaned.replace(/\s+[A-Z]{3,4}$/, '');

    return cleaned.trim();
  };

  useEffect(() => {
    if (isOpen) {
      setPlayer1Name(initialPlayer1Name);
      setPlayer2Name(initialPlayer2Name);
      setPlayer1Color(initialPlayer1Color);
      setPlayer2Color(initialPlayer2Color);
      setP1Details(initialP1Details);
      setP2Details(initialP2Details);
    }
  }, [isOpen, initialPlayer1Name, initialPlayer2Name, initialPlayer1Color, initialPlayer2Color, initialP1Details, initialP2Details]);

  // Cleanup timers bij sluiten
  useEffect(() => {
    return () => {
      if (interval1Ref.current) clearInterval(interval1Ref.current);
      if (interval2Ref.current) clearInterval(interval2Ref.current);
    };
  }, []);

  const fetchPlayerData = async (name: string, playerNum: 1 | 2) => {
    if (!name || name.trim().length < 3) return;

    const isP1 = playerNum === 1;
    const setLoading = isP1 ? setLoading1 : setLoading2;
    const setCounter = isP1 ? setCounter1 : setCounter2;
    const setDetails = isP1 ? setP1Details : setP2Details;
    const intervalRef = isP1 ? interval1Ref : interval2Ref;

    setLoading(true);
    setCounter(10);

    // Start counter interval (countdown from 10 to 0)
    intervalRef.current = setInterval(() => {
      setCounter(prev => Math.max(0, prev - 1));
    }, 1000);

    try {
      const response = await fetch(`${config.apiUrl}/api/v1/player/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name }),
      });
      if (!response.ok) throw new Error('Zoekopdracht mislukt');

      const data = await response.json();

      if (data.success && data.details) {
        const cleanedName = cleanPlayerName(data.details.name);

        // Map de scraper data naar het formaat dat we in de app gebruiken
        const details = {
          ...data.details,
          name: cleanedName,
          club: data.details.club || 'Onbekend',
          level: data.details.level || 0,
          countryRanking: data.details.countryRanking || '---',
          nlSquashGrade: data.details.nlSquashGrade || '---',
          worldRanking: data.details.worldRanking || '---'
        };

        // Update local names immediately for the onSave call
        const p1Name = isP1 ? cleanedName : player1Name;
        const p2Name = !isP1 ? cleanedName : player2Name;
        const p1D = isP1 ? details : p1Details;
        const p2D = !isP1 ? details : p2Details;

        // Auto-save and close
        onSave(p1Name, p2Name, player1Color, player2Color, p1D, p2D);
        onClose();
      } else {
        console.warn("Geen speler gevonden:", data.message);
      }
    } catch (error) {
      console.error("Fout bij ophalen spelerdata:", error);
    } finally {
      setLoading(false);
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
  };

  const handleClose = () => {
    onSave(player1Name, player2Name, player1Color, player2Color, p1Details, p2Details);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="timer-dialog-overlay">
      <div className="timer-dialog">
        <div className="timer-header">
          <h2>Player details</h2>
          <button className="close-button" onClick={handleClose}>×</button>
        </div>

        <div className="timer-content">
          <div className="player-customization-form">
            {targetPlayer === 1 && (
              <>
                <div className="player-customization-row">
                  <input
                    type="text"
                    className="player-customization-input"
                    value={player1Name}
                    onChange={(e) => {
                      setPlayer1Name(e.target.value);
                      setP1Details(null); // Clear results while typing
                    }}
                    onFocus={() => {
                      setPlayer1Name('');
                      setP1Details(null);
                    }}
                    onBlur={() => fetchPlayerData(player1Name, 1)}
                    placeholder="Player 1 Name"
                  />
                  <span
                    className="color-preview"
                    style={{ background: player1Color, cursor: 'pointer' }}
                    onClick={() => fetchPlayerData(player1Name, 1)}
                    title="Klik om te zoeken"
                  />
                  {loading1 && (
                    <div className="loader-container">
                      <div className="loader" />
                      <span
                        className="loader-text"
                        style={{ color: `hsl(${((counter1) / 10) * 120}, 100%, 40%)` }}
                      >
                        {counter1}
                      </span>
                    </div>
                  )}
                </div>

                <div className="player-details-found">
                  <div>Club: {p1Details?.club || '---'}</div>
                  <div>Level: {p1Details?.level || '---'}</div>
                  <div>GRADE: {p1Details?.nlSquashGrade || '---'}</div>
                  <div>NLD: {p1Details?.countryRanking || '---'}</div>
                  <div>World: {p1Details?.worldRanking || '---'}</div>
                </div>

                <div className="color-matrix">
                  {colorMatrix.map((color, idx) => (
                    <div
                      key={color + '-' + idx}
                      className={`color-matrix-square${player1Color === color ? ' selected' : ''}`}
                      style={{ background: color }}
                      onClick={() => setPlayer1Color(color)}
                    />
                  ))}
                </div>
              </>
            )}

            {targetPlayer === 2 && (
              <>
                <div className="player-customization-row">
                  <input
                    type="text"
                    className="player-customization-input"
                    value={player2Name}
                    onChange={(e) => {
                      setPlayer2Name(e.target.value);
                      setP2Details(null); // Clear results while typing
                    }}
                    onFocus={() => {
                      setPlayer2Name('');
                      setP2Details(null);
                    }}
                    onBlur={() => fetchPlayerData(player2Name, 2)}
                    placeholder="Player 2 Name"
                  />
                  <span
                    className="color-preview"
                    style={{ background: player2Color, cursor: 'pointer' }}
                    onClick={() => fetchPlayerData(player2Name, 2)}
                    title="Klik om te zoeken"
                  />
                  {loading2 && (
                    <div className="loader-container">
                      <div className="loader" />
                      <span
                        className="loader-text"
                        style={{ color: `hsl(${((counter2) / 10) * 120}, 100%, 40%)` }}
                      >
                        {counter2}
                      </span>
                    </div>
                  )}
                </div>

                <div className="player-details-found">
                  <div>Club: {p2Details?.club || '---'}</div>
                  <div>Level: {p2Details?.level || '---'}</div>
                  <div>GRADE: {p2Details?.nlSquashGrade || '---'}</div>
                  <div>NLD: {p2Details?.countryRanking || '---'}</div>
                  <div>World: {p2Details?.worldRanking || '---'}</div>
                </div>

                <div className="color-matrix">
                  {colorMatrix.map((color, idx) => (
                    <div
                      key={color + '-' + idx}
                      className={`color-matrix-square${player2Color === color ? ' selected' : ''}`}
                      style={{ background: color }}
                      onClick={() => setPlayer2Color(color)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerSettingsDialog;